/**
 * AI 응답의 화면 초안(draft = StudioSpec)을 실제 공통 컴포넌트(SearchPanel/SnAgGrid)가
 * 소비하는 메타데이터(snake_case sys_formctrl/sys_formgridcolumn 행)로 변환한다.
 * → 편집기 미리보기가 실제 배포 화면과 100% 동일한 디자인으로 렌더된다.
 */
import { global } from "@shared/libs/Global";
import { convertLoginData, getConvertedDate } from "@shared/utils/CommonUtils";
import type { DraftQueries, StudioSpec } from "./studioAiApi";

export interface PreviewGrid {
  /** sys_formgridcolumn.cd_grid (grid1=헤더, grid2=내역 …) */
  cdGrid: string;
  /** SnAgGrid columnConfig — sys_formgridcolumn 행 (snake_case) */
  columns: any[];
}

export interface PreviewMeta {
  /** SearchPanel data — 컨트롤 초기값 (getInitFormData의 formData와 동일 규칙) */
  formData: Record<string, any>;
  /** SearchPanel controls — sys_formctrl 행 (snake_case) */
  controls: any[];
  /** 그리드 목록 — cdGrid 오름차순 (sheet%는 제외). [0]=grid1(헤더) */
  grids: PreviewGrid[];
  /**
   * 그리드별 조회쿼리 초안 (key = cd_grid, value = SQL).
   * draft 안이 아니라 응답의 형제 필드로 오므로 attachQueries로 따로 주입된다.
   */
  queries?: DraftQueries;
}

/** camelCase 키 → snake_case 키 (cdFirm→cd_firm, qtRowindex→qt_rowindex) */
function toSnakeRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.keys(row).forEach((k) => {
    out[k.replace(/([A-Z])/g, "_$1").toLowerCase()] = row[k];
  });
  return out;
}

/** getInitFormData의 formData(main) 계산 로직과 동일 (CommonUtils.ts 참조) */
function buildFormData(controls: any[]): Record<string, any> {
  // 세션 만료 직후 등 userInfo가 비어 있을 수 있으므로 방어적으로 읽는다
  const loginInfo: any = global.getState().userInfo ?? {};
  const main: Record<string, any> = {
    cd_firm: loginInfo.cd_firm,
  };
  controls.forEach((col) => {
    const param: string = col.cd_param ?? "";
    const value: string = col.cd_value ?? "";
    if (!param) return;
    const p = param.split(",");

    if (col.tp_ctrl === "SnComboTextBox") {
      if (value && value.length >= 1) {
        const v = value.split(",");
        main[p[0].trim()] = convertLoginData(v[0]?.trim() ?? "");
        main[p[1]?.trim() ?? p[0].trim()] = convertLoginData(v[1]?.trim() ?? "");
      } else {
        p.forEach((k) => (main[k.trim()] = ""));
      }
    } else if (
      col.tp_ctrl === "SnDateBox" ||
      col.tp_ctrl === "SnDateMonthBox" ||
      col.tp_ctrl === "SnDateYearBox"
    ) {
      main[col.cd_param] = getConvertedDate(value);
    } else if (
      col.tp_ctrl === "SnPeriodBox" ||
      col.tp_ctrl === "SnPeriodMonthBox" ||
      col.tp_ctrl === "SnPeriodMonthBoxPop" ||
      col.tp_ctrl === "SnYearPeriodBox"
    ) {
      if (value && value.length >= 1) {
        const v = value.split(",");
        main[p[0].trim()] = getConvertedDate(v[0]?.trim());
        main[p[1]?.trim() ?? p[0].trim()] = getConvertedDate(v[1]?.trim());
      } else {
        p.forEach((k) => (main[k.trim()] = ""));
      }
    } else if (col.tp_ctrl === "SnComboPeriodBox" ||
      col.tp_ctrl === "SnCheckPeriodBox") {
      if (value && value.length >= 1) {
        const v = value.split(",");
        main[p[0].trim()] = convertLoginData(v[0]?.trim() ?? "");
        if (p[1]) main[p[1].trim()] = getConvertedDate(v[1]?.trim());
        if (p[2]) main[p[2].trim()] = getConvertedDate(v[2]?.trim());
      } else {
        p.forEach((k) => (main[k.trim()] = ""));
      }
    } else {
      if (value === "global" && Object.prototype.hasOwnProperty.call(loginInfo, col.cd_param)) {
        main[col.cd_param] = loginInfo[col.cd_param];
      } else {
        main[col.cd_param] = value ? convertLoginData(value) : "";
      }
    }
  });
  return main;
}

/**
 * SearchPanel의 드래그&드랍/삭제(X)가 no_line을 컨트롤 식별자(id/key)로 쓰므로
 * 미리보기 메타에서는 no_line을 항상 고유한 순번으로 재부여한다.
 * (중복이면 드래그 이동이 동작하지 않고, 삭제 시 엉뚱한 컨트롤이 숨겨진다)
 */
function assignUniqueNoLine(controls: any[]): any[] {
  controls.forEach((c, i) => {
    c.no_line = i + 1;
  });
  return controls;
}

/** cd_grid 정렬 비교자 — 접두어는 문자열로, 끝의 일련번호는 숫자로 비교 (grid2 < grid10) */
function byGridNo(a: string | null | undefined, b: string | null | undefined): number {
  const parse = (v: string | null | undefined) => {
    const s = String(v ?? "");
    const m = s.match(/^(.*?)(\d+)$/);
    return m ? { head: m[1], no: Number(m[2]) } : { head: s, no: Number.MAX_SAFE_INTEGER };
  };
  const x = parse(a);
  const y = parse(b);
  return x.head.localeCompare(y.head) || x.no - y.no;
}

/** draft(camelCase forms/grids) → 공통 컴포넌트 메타 */
export function specToPreviewMeta(spec: StudioSpec): PreviewMeta {
  const controls = assignUniqueNoLine(
    (spec.forms ?? [])
      .flatMap((f) => f?.controls ?? [])
      .map((c) => toSnakeRow(c as Record<string, unknown>))
  );

  // 전체 그리드를 cdGrid 오름차순으로 (sheet%로 시작하는 시트용 그리드는 제외)
  // 문자열 비교면 grid10이 grid2보다 앞서므로 끝의 숫자를 뽑아 숫자로 비교한다.
  const grids: PreviewGrid[] = (spec.grids ?? [])
    .filter((g) => g && !String(g.cdGrid ?? "").toLowerCase().startsWith("sheet"))
    .sort((a, b) => byGridNo(a?.cdGrid, b?.cdGrid))
    .map((g) => ({
      cdGrid: String(g?.cdGrid ?? "grid1"),
      columns: (g?.columns ?? []).map((c) => toSnakeRow(c as Record<string, unknown>)),
    }));

  return {
    formData: buildFormData(controls),
    controls,
    grids,
    // 조회쿼리(draftQueries)는 draft에 없다 (응답 형제 필드) — attachQueries로 주입된다
  };
}

/* ── 조회쿼리(draftQueries) 주입 ──
 * 조회쿼리는 draft(forms/grids) 안이 아니라 응답의 형제 필드로 오므로,
 * draft→메타 변환과 분리해서 나중에 붙인다.
 */

/** 변환이 끝난 메타에 그리드별 조회쿼리를 얹어 새 메타를 만든다 (메타 자체는 변형하지 않음) */
export function attachQueries(
  meta: PreviewMeta,
  queries?: DraftQueries | null
): PreviewMeta {
  if (!queries || Object.keys(queries).length === 0) return meta;
  return { ...meta, queries };
}

/** draft에 그릴 수 있는 forms/grids가 들어있는지 */
export function specHasMeta(
  spec: StudioSpec | null | undefined
): spec is StudioSpec {
  return Boolean(
    spec &&
      (spec.forms?.some((f) => f?.controls?.length) ||
        spec.grids?.some((g) => g?.columns?.length))
  );
}
